import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calendar, Clock, UserPlus, Loader2, CheckCircle2, IndianRupee, ListChecks, Plus,
  Phone, Users, Search, Package, CreditCard, Pill, Stethoscope, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PaymentReceipt from '../components/PaymentReceipt';
import PatientFamilyDrawer from '../components/PatientFamilyDrawer';
import TriageDrawer from '../components/TriageDrawer';
import ReceptionLayout from '../components/ReceptionLayout';
import { formatDateIST, todayIST } from '../utils/date';
import {
  isValidIndianMobile, isPartialIndianMobile,
  normalizeIndianMobile, phoneErrorMessage,
} from '../utils/phone';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

// Family relationships the receptionist can pick when registering a
// member on a phone that already has a primary patient. 'self' is
// reserved for the very first registration on a phone.
const RELATIONSHIPS = [
  'spouse', 'son', 'daughter', 'father', 'mother',
  'brother', 'sister', 'guardian', 'other',
];

export default function ReceptionPage() {
  const { user, logout } = useAuth();
  const today = todayIST();
  const [date, setDate] = useState(today);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [queue, setQueue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Patient flow state — phone-first, family-aware.
  const [pPhone, setPPhone] = useState('');
  // Whether the phone field has been focused-and-blurred at least
  // once. Suppresses the red border while the receptionist is
  // mid-keystroke on a fresh field.
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [matches, setMatches] = useState([]);          // patients sharing this phone
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState('new');             // 'pick' | 'new' | 'add_member'
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [pName, setPName] = useState('');
  const [pAge, setPAge] = useState('');
  const [pGender, setPGender] = useState('male');
  const [pRelationship, setPRelationship] = useState('self');
  const [bookingMode, setBookingMode] = useState('walkin'); // walkin | slot
  const [slot, setSlot] = useState('');
  const [fee, setFee] = useState('');
  const [feeTier, setFeeTier] = useState(null);       // 'first_visit' | 'followup' | 'walkin'
  const [payMethod, setPayMethod] = useState('cash'); // cash | card | upi | netbanking | wallet
  const [payRef, setPayRef] = useState('');            // last-4 / UPI txn ref
  const [paying, setPaying] = useState(false);
  const [receipt, setReceipt] = useState(null);        // shown after success

  // ---- Fee-exception / discount state ------------------------------
  // The receptionist can either grant a flat ₹ off or a %. Anything
  // exceeding the tenant's free-approval threshold (default 50%)
  // prompts for an admin PIN before the check-in is accepted.
  const [showDiscount, setShowDiscount] = useState(false);
  const [discKind, setDiscKind] = useState('percent');   // 'flat' | 'percent'
  const [discValue, setDiscValue] = useState('');        // string (input value)
  const [discReason, setDiscReason] = useState('');      // reason code
  const [discNote, setDiscNote] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [discCfg, setDiscCfg] = useState({ approval_threshold_percent: 50, reasons: [] });
  useEffect(() => {
    ax.get('/api/clinic-ops/discount/config')
      .then(({ data }) => setDiscCfg(data))
      .catch(() => {});
  }, []);

  // Hide /inventory button when tenant's pharmacy_mode='full' — legacy
  // PharmacyPage owns the inventory workflow there.
  const [showInventory, setShowInventory] = useState(false);
  useEffect(() => {
    ax.get('/api/clinic-ops/me/capabilities')
      .then(({ data }) => setShowInventory((data?.pharmacy_mode || 'lite') === 'lite'))
      .catch(() => setShowInventory(false));
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        ax.get(`/api/clinic-ops/sessions?date=${date}`),
        ax.get(`/api/clinic-ops/doctors`),
      ]);
      setSessions(sRes.data);
      setDoctors(dRes.data);
      if (!selectedSession && sRes.data.length) setSelectedSession(sRes.data[0]);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, [date]); // eslint-disable-line

  useEffect(() => {
    if (selectedSession?.id) {
      ax.get(`/api/clinic-ops/queue?session_id=${selectedSession.id}`)
        .then(r => setQueue(r.data)).catch(() => setQueue([]));
      if (!fee) setFee(String(selectedSession.consultation_fee || ''));
    }
    // eslint-disable-next-line
  }, [selectedSession?.id]);

  // Debounced family lookup on phone input — fires once the receptionist
  // has typed at least 4 digits, which keeps the network chatter down.
  // We search on the normalised number so paste of "+91 " prefixes still
  // finds the family without extra keystrokes.
  useEffect(() => {
    const digits = normalizeIndianMobile(pPhone);
    if (digits.length < 4) {
      setMatches([]); setMode('new'); setPRelationship('self'); setSelectedExisting(null);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await ax.get(`/api/clinic-ops/patients/search?phone=${encodeURIComponent(digits)}`);
        setMatches(r.data || []);
        if ((r.data || []).length === 0) {
          // No family on this phone yet — register a new primary.
          setMode('new'); setPRelationship('self'); setSelectedExisting(null);
        } else {
          setMode('pick'); setSelectedExisting(null);
        }
      } catch (_) { setMatches([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [pPhone]);

  // Fetch the doctor's suggested fee tier whenever the (doctor, patient)
  // pair changes — for a fresh registration patient_id is null which the
  // backend treats as first_visit. For a picked existing patient we send
  // the id so the backend can look up prior queue_entries.
  useEffect(() => {
    if (!selectedSession?.doctor_id) { setFeeTier(null); return; }
    const patientId = mode === 'pick' ? selectedExisting?.id : null;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ doctor_id: selectedSession.doctor_id });
        if (patientId) params.set('patient_id', patientId);
        const { data } = await ax.get(`/api/clinic-ops/fee?${params.toString()}`);
        if (cancelled) return;
        setFeeTier(data?.tier || null);
        if (data?.amount != null) setFee(String(data.amount));
      } catch (_) { /* keep manual fee */ }
    })();
    return () => { cancelled = true; };
  }, [selectedSession?.doctor_id, mode, selectedExisting?.id]);

  const resetPatientFields = () => {
    setPPhone(''); setPName(''); setPAge(''); setSlot('');
    setMatches([]); setMode('new'); setPRelationship('self'); setSelectedExisting(null);
    setPhoneTouched(false);
    // Also reset the discount block so the next patient starts clean.
    setShowDiscount(false); setDiscKind('percent'); setDiscValue('');
    setDiscReason(''); setDiscNote(''); setAdminPin('');
  };

  // Phone validity — computed each render. `phoneValid` gates submit,
  // `phoneShowError` gates the red-border UI (we suppress it while the
  // receptionist is still typing on a pristine field).
  const phoneValid = isValidIndianMobile(pPhone);
  const phoneShowError = (phoneTouched && !phoneValid)
    || (pPhone.length > 0 && !isPartialIndianMobile(pPhone));
  const phoneErrMsg = phoneShowError ? phoneErrorMessage(pPhone) : '';

  // ---- Discount preview math (mirrors the backend _compute_discount) ----
  // Kept as a plain derived object so any change to fee/discount inputs
  // repaints the summary + button label without extra state.
  const feeNum = Math.max(0, parseFloat(fee || 0) || 0);
  const rawDisc = Math.max(0, parseFloat(discValue || 0) || 0);
  const discPreview = (() => {
    if (!showDiscount || rawDisc <= 0) {
      return { pct: 0, amt: 0, final: feeNum, needsPin: false };
    }
    let amt, pct;
    if (discKind === 'percent') {
      pct = Math.min(100, rawDisc);
      amt = Math.round((feeNum * pct) / 100 * 100) / 100;
    } else {
      amt = Math.min(feeNum, rawDisc);
      pct = feeNum > 0 ? Math.round((amt / feeNum * 100) * 100) / 100 : (amt > 0 ? 100 : 0);
    }
    const final = Math.max(0, Math.round((feeNum - amt) * 100) / 100);
    return {
      pct, amt, final,
      needsPin: pct > (discCfg?.approval_threshold_percent ?? 50),
    };
  })();

  const checkin = async () => {
    if (!selectedSession) return toast.error('Pick a session');
    let patientPayload;
    if (mode === 'pick') {
      if (!selectedExisting) return toast.error('Pick a patient from the family');
      patientPayload = { patient_id: selectedExisting.id };
    } else {
      if (!pName.trim()) return toast.error('Patient name required');
      // Gate submit on a valid 10-digit Indian mobile. We flip
      // `phoneTouched` so the red border stays put after a failed
      // attempt — receptionist gets both the toast *and* the field
      // highlight (harder to miss on a crowded reception screen).
      if (!phoneValid) {
        setPhoneTouched(true);
        return toast.error(phoneErrorMessage(pPhone) || 'Enter a valid 10-digit Indian mobile number.');
      }
      patientPayload = {
        name: pName,
        phone: normalizeIndianMobile(pPhone),
        age: pAge ? parseInt(pAge, 10) : null,
        gender: pGender,
        relationship: pRelationship,
      };
    }
    setPaying(true);
    try {
      // Build the discount payload only when the receptionist actually
      // dialled in a discount — sending zeros would still create a
      // "waived at check-in (₹0)" audit row on the backend which we
      // don't want for regular full-pay visits.
      const discountPayload = (showDiscount && rawDisc > 0) ? {
        discount_amount: discKind === 'flat' ? rawDisc : 0,
        discount_percent: discKind === 'percent' ? rawDisc : 0,
        discount_reason: discReason || 'other',
        discount_note: discNote || null,
        admin_pin: discPreview.needsPin ? adminPin : null,
      } : null;
      if (discountPayload && discPreview.needsPin && !adminPin) {
        setPaying(false);
        return toast.error(`Admin PIN required for discounts above ${discCfg.approval_threshold_percent}%.`);
      }
      const res = await ax.post('/api/clinic-ops/checkin', {
        session_id: selectedSession.id,
        patient: patientPayload,
        booking_mode: bookingMode,
        slot_start: bookingMode === 'slot' ? slot : null,
        payment_amount: discPreview.final,   // what actually changes hands
        payment_method: payMethod,
        payment_reference: payRef || null,
        discount: discountPayload,
      });
      const queueNo = res?.data?.queue_no;
      const patientName = mode === 'pick'
        ? (selectedExisting?.name || '')
        : pName.trim();
      toast.success(`Patient checked in · Queue #${queueNo}`);
      // Show the printable receipt only when money actually changed hands.
      // For a fully-waived visit we skip the receipt (nothing collected)
      // but still show a toast so the receptionist has confirmation.
      const collected = discPreview.final;
      if (collected > 0) {
        setReceipt({
          amount: collected,
          method: payMethod,
          reference: payRef,
          patient_name: patientName,
          queue_no: queueNo,
          purpose: feeTier === 'first_visit' ? 'First consultation'
                  : feeTier === 'followup' ? 'Follow-up consultation'
                  : 'Consultation',
          doctor_name: docName(selectedSession.doctor_id),
          collected_by: user?.email,
        });
      }
      resetPatientFields();
      setPayRef('');
      const r = await ax.get(`/api/clinic-ops/queue?session_id=${selectedSession.id}`);
      setQueue(r.data);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Check-in failed');
    }
    setPaying(false);
  };

  const docName = (id) => doctors.find(d => d.id === id)?.name || '—';
  const [familyPatient, setFamilyPatient] = useState(null);
  const [triageEntry, setTriageEntry] = useState(null);

  return (
    <ReceptionLayout
      pageTitle="Reception Desk"
      pageSubtitle="Check in patients, manage today's queues and collect payments"
    >
      <div className="p-6" data-testid="reception-page">
      <main className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — Sessions list */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-zinc-500" />
              <span className="text-sm font-medium text-zinc-700">Sessions on</span>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="h-8 w-36 text-sm" data-testid="rec-date" />
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {loading ? <p className="text-sm text-zinc-500">Loading…</p>
                : sessions.length === 0 ? (
                  <div className="text-sm text-zinc-500 space-y-3">
                    <p>No sessions on this date yet.</p>
                    <StartSessionForm
                      doctors={doctors}
                      date={date}
                      defaultDoctorId={(user?.role === 'doctor' && doctors.find(d => d.email === user?.email)?.id) || null}
                      onCreated={refresh}
                    />
                  </div>
                )
                : sessions.map(s => (
                  <button key={s.id}
                    onClick={() => setSelectedSession(s)}
                    className={`w-full text-left p-3 border rounded-lg transition ${
                      selectedSession?.id === s.id ? 'border-blue-600 bg-blue-50' : 'hover:bg-zinc-50'
                    }`}
                    data-testid={`rec-session-${s.id}`}>
                    <div className="font-medium text-sm text-zinc-900">{docName(s.doctor_id)}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                      <Clock className="h-3 w-3" /> {s.start_time}–{s.end_time}
                      <span className="ml-auto bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded text-[10px] border border-amber-200">
                        Queue {s.queue_counter || 0}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-1">
                      {s.booked_count} booked · Fee ₹{s.consultation_fee || 0} · {s.source}
                    </div>
                  </button>
                ))}
            </div>
            {sessions.length > 0 && (
              <details className="mt-3 text-xs text-zinc-500" data-testid="rec-add-session-toggle">
                <summary className="cursor-pointer select-none hover:text-zinc-700">+ Start another session</summary>
                <div className="mt-3">
                  <StartSessionForm
                    doctors={doctors}
                    date={date}
                    defaultDoctorId={(user?.role === 'doctor' && doctors.find(d => d.email === user?.email)?.id) || null}
                    onCreated={refresh}
                  />
                </div>
              </details>
            )}
          </div>
        </div>

        {/* CENTER — Check-in form */}
        <div className="lg:col-span-1 bg-white border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="h-4 w-4 text-blue-700" />
            <h3 className="font-semibold text-zinc-900">Check-in patient</h3>
          </div>
          {!selectedSession ? (
            <p className="text-sm text-zinc-500">Pick a session on the left.</p>
          ) : (
            <div className="space-y-3">
              {/* PHONE — primary key. Family lookup runs as you type. */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <Phone className="h-3 w-3" /> Phone number *
                </Label>
                <div className="relative">
                  <Input
                    value={pPhone}
                    onChange={(e) => {
                      // Keep the user's raw text in state so their caret
                      // doesn't jump when they paste "+91 " prefixes —
                      // we normalise on submit, not on every keystroke.
                      setPPhone(e.target.value);
                    }}
                    onBlur={() => setPhoneTouched(true)}
                    placeholder="10-digit phone"
                    inputMode="tel"
                    maxLength={16}
                    autoComplete="off"
                    aria-invalid={phoneShowError || undefined}
                    className={phoneShowError
                      ? 'border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500'
                      : ''}
                    data-testid="rec-pphone"
                  />
                  {searching && (
                    <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-zinc-400" />
                  )}
                </div>
                {phoneShowError && (
                  <p className="text-[11px] text-red-600 flex items-center gap-1"
                     data-testid="rec-pphone-error">
                    {phoneErrMsg}
                  </p>
                )}
              </div>

              {/* FAMILY card — shown when patients exist on this phone. */}
              {matches.length > 0 && (
                <div className="border rounded-lg overflow-hidden" data-testid="rec-family-card">
                  <div className="bg-zinc-50 border-b px-3 py-2 flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-zinc-500" />
                    <span className="text-xs font-medium text-zinc-700">
                      {matches.length} patient{matches.length > 1 ? 's' : ''} on this phone
                    </span>
                  </div>
                  <div className="divide-y">
                    {matches.map(p => {
                      const active = selectedExisting?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { setMode('pick'); setSelectedExisting(p); }}
                          className={`w-full text-left px-3 py-2 transition ${
                            active ? 'bg-blue-50' : 'hover:bg-zinc-50'
                          }`}
                          data-testid={`rec-family-row-${p.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-zinc-900 truncate">{p.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                              p.is_primary
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-zinc-50 text-zinc-600 border-zinc-200'
                            }`}>
                              {p.is_primary ? 'Primary' : p.relationship}
                            </span>
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            {p.age ? `${p.age}y · ` : ''}{p.gender || '—'}
                            {' · last visit: '}
                            {p.last_visit_at
                              ? formatDateIST(p.last_visit_at)
                              : 'never'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setMode('add_member'); setSelectedExisting(null); setPRelationship('spouse'); }}
                    className="w-full text-left px-3 py-2 text-xs text-blue-700 hover:bg-blue-50 border-t flex items-center gap-1"
                    data-testid="rec-add-member"
                  >
                    <Plus className="h-3 w-3" /> Add family member on this phone
                  </button>
                </div>
              )}

              {/* NEW patient form — for 'new' primary OR 'add_member'. */}
              {(mode === 'new' || mode === 'add_member') && (
                <div className="space-y-3 pt-1" data-testid="rec-new-patient-form">
                  <div className="space-y-1.5">
                    <Label>Name *</Label>
                    <Input value={pName} onChange={(e) => setPName(e.target.value)} data-testid="rec-pname" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Age</Label>
                      <Input value={pAge} onChange={(e) => setPAge(e.target.value)} type="number" data-testid="rec-page" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Gender</Label>
                      <Select value={pGender} onValueChange={setPGender}>
                        <SelectTrigger data-testid="rec-pgender"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {mode === 'add_member' && (
                    <div className="space-y-1.5">
                      <Label>Relationship to primary</Label>
                      <Select value={pRelationship} onValueChange={setPRelationship}>
                        <SelectTrigger data-testid="rec-prelationship"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RELATIONSHIPS.map(r => (
                            <SelectItem key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="space-y-1.5">
                  <Label>Booking</Label>
                  <Select value={bookingMode} onValueChange={setBookingMode}>
                    <SelectTrigger data-testid="rec-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walkin">Walk-in (queue #)</SelectItem>
                      <SelectItem value="slot">Time slot</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {bookingMode === 'slot' && (
                  <div className="space-y-1.5">
                    <Label>Slot (HH:MM)</Label>
                    <Input value={slot} onChange={(e) => setSlot(e.target.value)} placeholder="09:30" data-testid="rec-slot" />
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <IndianRupee className="h-3 w-3" /> Consultation fee
                  {feeTier && (
                    <span
                      className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full border ${
                        feeTier === 'first_visit' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : feeTier === 'followup' ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-zinc-50 text-zinc-600 border-zinc-200'
                      }`}
                      data-testid="rec-fee-tier"
                    >
                      {feeTier === 'first_visit' ? 'First visit'
                       : feeTier === 'followup' ? 'Follow-up'
                       : 'Walk-in / repeat'}
                    </span>
                  )}
                </Label>
                <Input value={fee} onChange={(e) => setFee(e.target.value)} type="number" data-testid="rec-fee" />
              </div>

              {/* --- Fee exception / discount block ------------------- */}
              <div className="border border-dashed border-zinc-200 rounded-lg px-3 py-2.5 bg-zinc-50/50">
                {!showDiscount ? (
                  <button
                    type="button"
                    onClick={() => setShowDiscount(true)}
                    className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1.5"
                    data-testid="rec-discount-open"
                  >
                    <IndianRupee className="h-3 w-3" /> Add fee exception / discount
                  </button>
                ) : (
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Fee exception</Label>
                      <button
                        type="button"
                        onClick={() => { setShowDiscount(false); setDiscValue(''); setDiscReason(''); setDiscNote(''); setAdminPin(''); }}
                        className="text-[10px] text-zinc-500 hover:text-zinc-800"
                        data-testid="rec-discount-close"
                      >Clear</button>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white rounded-md p-0.5 border w-fit">
                      <button
                        type="button"
                        onClick={() => setDiscKind('flat')}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded ${discKind === 'flat' ? 'bg-blue-600 text-white' : 'text-zinc-600'}`}
                        data-testid="rec-discount-kind-flat"
                      >Flat ₹</button>
                      <button
                        type="button"
                        onClick={() => setDiscKind('percent')}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded ${discKind === 'percent' ? 'bg-blue-600 text-white' : 'text-zinc-600'}`}
                        data-testid="rec-discount-kind-percent"
                      >Percent %</button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wide text-zinc-500">
                          {discKind === 'flat' ? 'Discount ₹' : 'Discount %'}
                        </Label>
                        <Input
                          type="number"
                          value={discValue}
                          onChange={(e) => setDiscValue(e.target.value)}
                          placeholder={discKind === 'flat' ? '50' : '20'}
                          data-testid="rec-discount-value"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Reason</Label>
                        <Select value={discReason} onValueChange={setDiscReason}>
                          <SelectTrigger data-testid="rec-discount-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                          <SelectContent>
                            {(discCfg.reasons || []).map(r => (
                              <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Note (optional)</Label>
                      <Input
                        value={discNote}
                        onChange={(e) => setDiscNote(e.target.value)}
                        placeholder="e.g. IIT staff card #4521, employee ID, referrer name…"
                        data-testid="rec-discount-note"
                      />
                    </div>
                    {discPreview.needsPin && (
                      <div className="space-y-1 pt-1 border-t border-dashed border-amber-300">
                        <Label className="text-[10px] uppercase tracking-wide text-amber-700 font-bold flex items-center gap-1">
                          Admin PIN required — over {discCfg.approval_threshold_percent}% discount
                        </Label>
                        <Input
                          type="password"
                          inputMode="numeric"
                          maxLength={8}
                          value={adminPin}
                          onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ''))}
                          placeholder="4–8 digit PIN"
                          data-testid="rec-discount-pin"
                        />
                      </div>
                    )}
                    {rawDisc > 0 && (
                      <div className="text-[11px] bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5 flex items-center justify-between" data-testid="rec-discount-summary">
                        <span className="text-blue-900">
                          <b>{discPreview.pct}% off</b> · ₹{discPreview.amt.toFixed(0)} discount
                        </span>
                        <span className="font-bold text-blue-950">
                          Collect ₹{discPreview.final.toFixed(0)}
                          {discPreview.final === 0 && <span className="ml-1 text-emerald-700">· Waived</span>}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5 text-xs">
                    <CreditCard className="h-3 w-3" /> Pay method
                  </Label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger data-testid="rec-pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="netbanking">Net banking</SelectItem>
                      <SelectItem value="wallet">Wallet</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {payMethod !== 'cash' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Reference / last-4</Label>
                    <Input value={payRef} onChange={(e) => setPayRef(e.target.value)}
                      placeholder={payMethod === 'upi' ? 'UPI ref #' : payMethod === 'card' ? 'Last-4' : 'Txn ref'}
                      data-testid="rec-pay-ref" />
                  </div>
                )}
              </div>
              <Button onClick={checkin} disabled={paying || (mode === 'pick' && !selectedExisting)}
                className="w-full bg-blue-700 hover:bg-blue-800" data-testid="rec-submit">
                {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {(() => {
                  const collect = showDiscount && rawDisc > 0 ? discPreview.final : feeNum;
                  const waived = showDiscount && rawDisc > 0 && discPreview.final === 0;
                  const namePart = mode === 'pick' && selectedExisting
                    ? `Check in ${selectedExisting.name}`
                    : 'Register';
                  if (waived) return `${namePart} · Waived (₹0)`;
                  return `${namePart} & collect ₹${collect.toFixed(0)}`;
                })()}
              </Button>
            </div>
          )}
        </div>

        {/* RIGHT — Queue */}
        <div className="lg:col-span-1 bg-white border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-zinc-900">Today's queue</h3>
            <span className="text-xs text-zinc-500">{queue.length} patients</span>
          </div>
          <div className="space-y-1.5 max-h-[70vh] overflow-y-auto">
            {queue.length === 0 ? (
              <p className="text-sm text-zinc-500">No check-ins yet.</p>
            ) : queue.map(q => (
              <div key={q.id} className="flex items-center gap-2 border rounded-lg px-3 py-2 hover:bg-zinc-50 transition group"
                data-testid={`rec-queue-${q.id}`}>
                <div className="h-8 w-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                  {q.queue_no}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-900 truncate flex items-center gap-1.5 flex-wrap">
                    {q.patient_name}
                    {q.triage?.vitals && Object.keys(q.triage.vitals).length > 0 && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" title="Vitals recorded">✓ vitals</span>
                    )}
                    {q.discount_summary && (
                      <span
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${
                          q.discount_summary.waived
                            ? 'bg-amber-50 text-amber-800 border-amber-300'
                            : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        }`}
                        title={
                          `${q.discount_summary.waived ? 'Waived' : `${q.discount_summary.discount_percent}% off`}` +
                          (q.discount_summary.reason ? ` · ${q.discount_summary.reason}` : '') +
                          (q.discount_summary.note ? ` — ${q.discount_summary.note}` : '')
                        }
                        data-testid={`rec-queue-discount-${q.id}`}
                      >
                        {q.discount_summary.waived
                          ? '🎫 Waived'
                          : `${q.discount_summary.discount_percent}% off`}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    {q.booking_mode}{q.slot_start ? ` · ${q.slot_start}` : ''}
                    {q.payment_status === 'paid' ? ` · paid ₹${q.payment_amount}` : ''}
                    {q.payment_status === 'waived' ? ` · waived (₹0)` : ''}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] opacity-0 group-hover:opacity-100 transition"
                  onClick={() => setTriageEntry(q)} data-testid={`rec-triage-${q.id}`}>
                  <Stethoscope className="h-3 w-3 mr-1" /> Triage
                </Button>
                {q.status !== 'completed' && q.status !== 'in_consult' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-1.5 text-[10px] opacity-0 group-hover:opacity-100 transition text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      // Two-step confirm so receptionists don't trash a token by accident — completed
                      // visits are already blocked at the backend but this catches the merely-paid case.
                      const label = `${q.patient_name} (Queue #${q.queue_no})`;
                      if (!window.confirm(`Cancel this queue entry?\n\n${label}\n\nThe token will be marked cancelled and removed from today's queue. The patient's profile is preserved.`)) return;
                      try {
                        await ax.delete(`/api/clinic-ops/queue/${q.id}`);
                        toast.success(`Token #${q.queue_no} cancelled`);
                        // Optimistic refresh so the row disappears immediately.
                        if (selectedSession) {
                          const r = await ax.get(`/api/clinic-ops/queue?session_id=${selectedSession.id}`);
                          setQueue(r.data || []);
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Could not cancel');
                      }
                    }}
                    data-testid={`rec-cancel-${q.id}`}
                    title="Cancel this queue token"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  q.status === 'completed' ? 'bg-emerald-50 text-emerald-700'
                  : q.status === 'in_consult' ? 'bg-amber-50 text-amber-700'
                  : 'bg-zinc-100 text-zinc-700'
                }`}>{q.status}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
      {receipt && <PaymentReceipt data={receipt} onClose={() => setReceipt(null)} />}
      <PatientFamilyDrawer
        open={!!familyPatient}
        patient={familyPatient}
        onClose={() => setFamilyPatient(null)}
        onPick={(p) => {
          setSelectedExisting(p);
          setMode('pick');
          setPPhone(p.phone || '');
          setFamilyPatient(null);
        }}
      />
      <TriageDrawer
        open={!!triageEntry}
        queueEntry={triageEntry}
        onClose={() => setTriageEntry(null)}
        onSaved={() => {
          // Re-fetch queue so the freshly-saved triage badge appears.
          if (selectedSession?.id) {
            ax.get(`/api/clinic-ops/queue?session_id=${selectedSession.id}`)
              .then(r => setQueue(r.data)).catch(() => {});
          }
          setTriageEntry(null);
        }}
      />
      </div>
    </ReceptionLayout>
  );
}

/**
 * Inline "start a session now" form — lets a doctor (or reception staff)
 * spin up an ad-hoc consultation session right from the queue panel
 * without round-tripping to the admin portal to build a recurring
 * template. Pre-selects the doctor when there's only one in the clinic.
 */
function StartSessionForm({ doctors, date, defaultDoctorId, onCreated }) {
  const initialDoctor = defaultDoctorId
    || (doctors.length === 1 ? doctors[0].id : '');
  const [doctorId, setDoctorId] = useState(initialDoctor);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:00');
  const [fee, setFee] = useState('300');
  const [busy, setBusy] = useState(false);

  if (doctors.length === 0) {
    return <p className="text-xs text-amber-700">No doctors found in this clinic — add one in <code>/admin/users</code> first.</p>;
  }

  const submit = async () => {
    if (!doctorId) return toast.error('Pick a doctor');
    setBusy(true);
    try {
      await ax.post('/api/clinic-ops/sessions', {
        doctor_id: doctorId,
        name: 'Walk-in clinic',
        date,
        start_time: start,
        end_time: end,
        slot_minutes: 15,
        consultation_fee: parseFloat(fee || 0),
      });
      toast.success('Session started — you can register patients now');
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start session');
    }
    setBusy(false);
  };

  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-2" data-testid="rec-start-session-form">
      {doctors.length > 1 ? (
        <Select value={doctorId} onValueChange={setDoctorId}>
          <SelectTrigger className="h-8 text-xs" data-testid="rec-start-doctor"><SelectValue placeholder="Doctor" /></SelectTrigger>
          <SelectContent>
            {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-zinc-600">Doctor: <span className="font-medium">{doctors[0]?.name}</span></p>
      )}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px]">Start</Label>
          <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 text-xs" data-testid="rec-start-time" />
        </div>
        <div>
          <Label className="text-[10px]">End</Label>
          <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 text-xs" data-testid="rec-end-time" />
        </div>
        <div>
          <Label className="text-[10px]">Fee ₹</Label>
          <Input type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} className="h-8 text-xs" data-testid="rec-fee" />
        </div>
      </div>
      <Button size="sm" onClick={submit} disabled={busy} className="w-full bg-blue-700 hover:bg-blue-800"
        data-testid="rec-start-session-submit">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
        Start session
      </Button>
    </div>
  );
}
